import React, { useEffect, useRef } from 'react';

interface VisualizerProps {
  getVisualizerData: () => Uint8Array;
  isRecording: boolean;
}

export const Visualizer: React.FC<VisualizerProps> = ({ getVisualizerData, isRecording }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Handle high DPI displays
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    const draw = () => {
      if (!isRecording) {
        ctx.clearRect(0, 0, rect.width, rect.height);
        return;
      }

      const dataArray = getVisualizerData();
      const bufferLength = dataArray.length;
      
      ctx.clearRect(0, 0, rect.width, rect.height);

      const barWidth = (rect.width / bufferLength) * 2.5;
      let barHeight;
      let x = 0;

      for (let i = 0; i < bufferLength; i++) {
        barHeight = (dataArray[i] / 255) * rect.height;

        // Modern Gradient: Dark Gray to Transparent
        const gradient = ctx.createLinearGradient(0, rect.height - barHeight, 0, rect.height);
        gradient.addColorStop(0, '#1f2937'); // gray-800
        gradient.addColorStop(1, '#9ca3af'); // gray-400

        ctx.fillStyle = gradient;
        
        // Rounded bars logic (simplified)
        ctx.fillRect(x, rect.height - barHeight, barWidth, barHeight);

        x += barWidth + 2; // Increased gap
      }

      animationRef.current = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [getVisualizerData, isRecording]);

  return (
    <canvas
      ref={canvasRef}
      className="w-full h-full"
    />
  );
};
